/**
 * The console's icon vocabulary.
 *
 * Hand-drawn 16px-grid strokes rather than an icon dependency: the set is small, it never
 * needs to be tree-shaken, and it cannot break on an upgrade. `currentColor` throughout so
 * an icon inherits the semantic colour of whatever it sits inside.
 *
 * Icons are decorative by default (aria-hidden). Pass `title` only when the icon is the
 * ONLY thing conveying the meaning — an icon-only button should carry an aria-label on the
 * button instead, which is what <Button icon> does.
 */
import type { CSSProperties, ReactElement } from 'react';

import type { IconName } from './iconNames.ts';
export type { IconName };

const PATHS: Record<IconName, ReactElement> = {
  moon: <path d="M13.5 9.6A5.6 5.6 0 0 1 6.4 2.5a5.8 5.8 0 1 0 7.1 7.1Z" />,
  phone: (
    <path d="M3.2 2.6h2.3l1.1 2.8-1.4 1a8.4 8.4 0 0 0 3.9 3.9l1-1.4 2.8 1.1v2.3a1 1 0 0 1-1.1 1A11 11 0 0 1 2.2 3.7a1 1 0 0 1 1-1.1Z" />
  ),
  'phone-live': (
    <>
      <path d="M2.6 2.6h2l1 2.4-1.2.9a7.3 7.3 0 0 0 3.4 3.4l.9-1.2 2.4 1v2a.9.9 0 0 1-1 .9A9.6 9.6 0 0 1 1.7 3.6a.9.9 0 0 1 .9-1Z" />
      <path d="M11.4 2.2a4.6 4.6 0 0 1 2.4 2.4M11.9 5.6a1.6 1.6 0 0 0-.9-.9" />
    </>
  ),
  'alert-triangle': (
    <>
      <path d="M8 2.3 14.4 13H1.6L8 2.3Z" />
      <path d="M8 6.6v3M8 11.6h.01" />
    </>
  ),
  bell: (
    <>
      <path d="M4 6.6a4 4 0 1 1 8 0c0 3 1.1 4 1.1 4H2.9s1.1-1 1.1-4Z" />
      <path d="M6.6 13a1.6 1.6 0 0 0 2.8 0" />
    </>
  ),
  'shield-check': (
    <>
      <path d="M8 1.7 13.2 4v4c0 3.2-2.4 5.4-5.2 6.3C5.2 13.4 2.8 11.2 2.8 8V4L8 1.7Z" />
      <path d="m5.9 7.9 1.5 1.5 2.8-2.9" />
    </>
  ),
  inbox: (
    <>
      <path d="M1.9 8.6h3l.9 1.7h4.4l.9-1.7h3" />
      <path d="M3.4 2.8h9.2l1.5 5.8v3.6a1 1 0 0 1-1 1H2.9a1 1 0 0 1-1-1V8.6l1.5-5.8Z" />
    </>
  ),
  bolt: <path d="M9 1.6 3.4 9.2h4L7 14.4l5.6-7.6h-4l.4-5.2Z" />,
  sliders: (
    <>
      <path d="M2.4 4.6h11.2M2.4 11.4h11.2" />
      <circle cx="6" cy="4.6" r="1.7" />
      <circle cx="10.4" cy="11.4" r="1.7" />
    </>
  ),
  plug: (
    <>
      <path d="M6 1.8v3.4M10 1.8v3.4" />
      <path d="M4 5.2h8v2.2a4 4 0 0 1-4 4 4 4 0 0 1-4-4V5.2Z" />
      <path d="M8 11.4v2.8" />
    </>
  ),
  card: (
    <>
      <rect x="1.7" y="3.4" width="12.6" height="9.2" rx="1.4" />
      <path d="M1.7 6.6h12.6M4.2 10h2.6" />
    </>
  ),
  chart: (
    <>
      <path d="M2.2 13.6h11.6" />
      <path d="M4.4 13.6V8.2M7.6 13.6V4.4M10.8 13.6v-3.6" />
    </>
  ),
  gear: (
    <>
      <circle cx="8" cy="8" r="2.2" />
      <path d="M12.9 9.8a1.1 1.1 0 0 0 .2 1.2l.1.1a1.3 1.3 0 1 1-1.9 1.9l-.1-.1a1.1 1.1 0 0 0-1.2-.2 1.1 1.1 0 0 0-.7 1v.2a1.3 1.3 0 0 1-2.6 0v-.1a1.1 1.1 0 0 0-.7-1 1.1 1.1 0 0 0-1.2.2l-.1.1a1.3 1.3 0 1 1-1.9-1.9l.1-.1a1.1 1.1 0 0 0 .2-1.2 1.1 1.1 0 0 0-1-.7h-.2a1.3 1.3 0 0 1 0-2.6h.1a1.1 1.1 0 0 0 1-.7 1.1 1.1 0 0 0-.2-1.2l-.1-.1a1.3 1.3 0 1 1 1.9-1.9l.1.1a1.1 1.1 0 0 0 1.2.2h.1a1.1 1.1 0 0 0 .7-1v-.2a1.3 1.3 0 0 1 2.6 0v.1a1.1 1.1 0 0 0 .7 1 1.1 1.1 0 0 0 1.2-.2l.1-.1a1.3 1.3 0 1 1 1.9 1.9l-.1.1a1.1 1.1 0 0 0-.2 1.2v.1a1.1 1.1 0 0 0 1 .7h.2a1.3 1.3 0 0 1 0 2.6h-.1a1.1 1.1 0 0 0-1 .7Z" />
    </>
  ),
  book: (
    <>
      <path d="M2.4 3.2A1.4 1.4 0 0 1 3.8 1.8H13v10.4H3.8a1.4 1.4 0 0 0-1.4 1.4V3.2Z" />
      <path d="M2.4 12.2a1.4 1.4 0 0 0 1.4 1.4H13" />
    </>
  ),
  search: (
    <>
      <circle cx="7.2" cy="7.2" r="4.4" />
      <path d="m10.5 10.5 3 3" />
    </>
  ),
  close: <path d="m3.8 3.8 8.4 8.4M12.2 3.8l-8.4 8.4" />,
  check: <path d="m3 8.4 3.2 3.2L13 4.8" />,
  info: (
    <>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M8 7.4v3.8M8 5.1h.01" />
    </>
  ),
  refresh: (
    <>
      <path d="M13.4 6.8A5.6 5.6 0 0 0 3.6 4.4L2.2 5.8" />
      <path d="M2.6 9.2a5.6 5.6 0 0 0 9.8 2.4l1.4-1.4" />
      <path d="M2.2 2.6v3.2h3.2M13.8 13.4v-3.2h-3.2" />
    </>
  ),
  external: (
    <>
      <path d="M9.4 2.6h4v4" />
      <path d="M13.4 2.6 7.8 8.2" />
      <path d="M12 9.2v3.2a1 1 0 0 1-1 1H3.6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3.2" />
    </>
  ),
  lock: (
    <>
      <rect x="3.2" y="7" width="9.6" height="6.6" rx="1.3" />
      <path d="M5.6 7V5.2a2.4 2.4 0 0 1 4.8 0V7" />
    </>
  ),
  sun: (
    <>
      <circle cx="8" cy="8" r="3" />
      <path d="M8 1.6v1.6M8 12.8v1.6M1.6 8h1.6M12.8 8h1.6M3.5 3.5l1.1 1.1M11.4 11.4l1.1 1.1M12.5 3.5l-1.1 1.1M4.6 11.4l-1.1 1.1" />
    </>
  ),
  menu: <path d="M2.4 4.4h11.2M2.4 8h11.2M2.4 11.6h11.2" />,
  'chevron-right': <path d="m6 3.4 4.4 4.6L6 12.6" />,
  'chevron-down': <path d="m3.4 6 4.6 4.4L12.6 6" />,
  'chevron-up': <path d="m3.4 10 4.6-4.4 4.6 4.4" />,
  'arrow-right': (
    <>
      <path d="M2.6 8h10.8" />
      <path d="m9.4 4 4 4-4 4" />
    </>
  ),
  plus: <path d="M8 3v10M3 8h10" />,
  filter: <path d="M2.2 3.4h11.6L9.4 8.4v4.4l-2.8-1.6V8.4L2.2 3.4Z" />,
  download: (
    <>
      <path d="M8 2.4v7.4" />
      <path d="m4.8 7 3.2 3.2L11.2 7" />
      <path d="M2.6 12.8h10.8" />
    </>
  ),
  clock: (
    <>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M8 4.4V8l2.4 1.4" />
    </>
  ),
  user: (
    <>
      <circle cx="8" cy="5.6" r="2.8" />
      <path d="M2.8 13.6a5.2 5.2 0 0 1 10.4 0" />
    </>
  ),
  users: (
    <>
      <circle cx="6" cy="5.6" r="2.6" />
      <path d="M1.6 13.4a4.4 4.4 0 0 1 8.8 0" />
      <path d="M10.6 3.4a2.6 2.6 0 0 1 0 4.4M11.6 9.6a4.4 4.4 0 0 1 2.8 3.8" />
    </>
  ),
  logout: (
    <>
      <path d="M6.2 13.4H3.4a1 1 0 0 1-1-1V3.6a1 1 0 0 1 1-1h2.8" />
      <path d="M10 11.2 13.2 8 10 4.8" />
      <path d="M13.2 8H6" />
    </>
  ),
  copy: (
    <>
      <rect x="5.4" y="5.4" width="8.2" height="8.2" rx="1.2" />
      <path d="M10.6 5.4V3.6a1.2 1.2 0 0 0-1.2-1.2H3.6a1.2 1.2 0 0 0-1.2 1.2v5.8a1.2 1.2 0 0 0 1.2 1.2h1.8" />
    </>
  ),
  flag: (
    <>
      <path d="M3.4 14V2.2" />
      <path d="M3.4 3h7.2l-1.2 2.6L10.6 8.2H3.4" />
    </>
  ),
  ticket: (
    <>
      <path d="M1.8 6.2V4.4a1 1 0 0 1 1-1h10.4a1 1 0 0 1 1 1v1.8a2 2 0 0 0 0 3.6v1.8a1 1 0 0 1-1 1H2.8a1 1 0 0 1-1-1V9.8a2 2 0 0 0 0-3.6Z" />
      <path d="M9.4 3.4v9.2" />
    </>
  ),
  message: <path d="M13.6 9.2a1.4 1.4 0 0 1-1.4 1.4H5.2l-2.8 2.8V3.6a1.4 1.4 0 0 1 1.4-1.4h8.4a1.4 1.4 0 0 1 1.4 1.4v5.6Z" />,
  sparkle: (
    <>
      <path d="m8 1.8 1.6 4.6 4.6 1.6-4.6 1.6L8 14.2l-1.6-4.6L1.8 8l4.6-1.6L8 1.8Z" />
    </>
  ),
  eye: (
    <>
      <path d="M1.4 8S3.8 3.6 8 3.6 14.6 8 14.6 8 12.2 12.4 8 12.4 1.4 8 1.4 8Z" />
      <circle cx="8" cy="8" r="2" />
    </>
  ),
  sidebar: (
    <>
      <rect x="1.8" y="2.6" width="12.4" height="10.8" rx="1.3" />
      <path d="M6.2 2.6v10.8" />
    </>
  ),
  'empty-box': (
    <>
      <path d="M1.8 5.6 8 2.4l6.2 3.2v5L8 13.8l-6.2-3.2v-5Z" />
      <path d="M1.8 5.6 8 8.8l6.2-3.2M8 8.8v5" />
    </>
  ),
  'wifi-off': (
    <>
      <path d="M2 2 14 14" />
      <path d="M4.6 8.6a6.4 6.4 0 0 1 2.2-1.3M1.8 5.8A10 10 0 0 1 5 3.9M14.2 5.8a10 10 0 0 0-6.6-2.4M11.6 8.6a6.4 6.4 0 0 0-1.4-.9" />
      <path d="M8 11.8h.01" />
    </>
  ),
  help: (
    <>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M6.2 6.2a1.9 1.9 0 0 1 3.7.6c0 1.3-1.9 1.9-1.9 1.9" />
      <path d="M8 11.4h.01" />
    </>
  ),
  'chevron-left': <path d="M10 3.4 5.6 8l4.4 4.6" />,
  tower: (
    <>
      <path d="M8 7.4a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
      <path d="M7.6 7.3 6 13.8M8.4 7.3 10 13.8M6.6 11h2.8" />
      <path d="M4.8 2.6a5 5 0 0 0 0 6.7M11.2 2.6a5 5 0 0 1 0 6.7" />
    </>
  ),
  speaker: (
    <>
      <path d="M3 6.2h2.2L8.6 3.4v9.2L5.2 9.8H3Z" />
      <path d="M10.8 6.1a2.9 2.9 0 0 1 0 3.8M12.6 4.4a5.4 5.4 0 0 1 0 7.2" />
    </>
  ),
  pin: (
    <>
      <path d="M8 14s4.6-4.2 4.6-7.4A4.6 4.6 0 0 0 3.4 6.6C3.4 9.8 8 14 8 14Z" />
      <circle cx="8" cy="6.5" r="1.7" />
    </>
  ),
  edit: <path d="M11.4 2.6 13.4 4.6 5.6 12.4 2.4 13.6 3.6 10.4Z" />,
  play: <path d="M5.4 3.4 12.2 8l-6.8 4.6Z" />,
  dots: (
    <>
      <circle cx="3.4" cy="8" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="8" cy="8" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="12.6" cy="8" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),
  shield: <path d="M8 2 13 4v4.1c0 3-2 5-5 5.9-3-.9-5-2.9-5-5.9V4Z" />,
  'arrow-down': (
    <>
      <path d="M8 2.8v10.4" />
      <path d="M4.4 9.6 8 13.2l3.6-3.6" />
    </>
  ),
  minus: <path d="M3.4 8h9.2" />,
};

export type IconProps = {
  name: IconName;
  size?: number;
  className?: string;
  /** Only when the icon alone carries the meaning; otherwise leave it decorative. */
  title?: string;
  strokeWidth?: number;
  style?: CSSProperties;
};

export function Icon({ name, size = 16, className, title, strokeWidth = 1.5, style }: IconProps): ReactElement {
  return (
    <svg
      className={className}
      style={{ flex: '0 0 auto', ...style }}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {PATHS[name]}
    </svg>
  );
}

export default Icon;
